import type {
  ProtectionResponseApi,
  ProtectionResponse,
  ProtectionObservationResponse,
  TwilioComparisonResponseApi,
  TwilioComparisonResponse,
} from '@/types/Api/SmsProtection'
import type { ProtectionObservation } from '@/types/Models/SmsProtection'

export default class SmsProtectionTransformer {
  static observation(data: ProtectionObservationResponse): ProtectionObservation {
    return {
      id: data.id,
      attemptId: data.attempt_id,
      companyId: data.company_id,
      itemId: data.item_id,
      shipmentNumber: data.shipment_number,
      notificationType: data.notification_type,
      recipientMask: data.recipient_mask,
      country: data.country,
      decision: data.decision,
      reasons: data.reasons,
      evidence: data.evidence,
      segments: data.segments,
      estimatedCostUsd: data.estimated_cost_usd,
      providerStatus: data.provider_status,
      providerSid: data.provider_sid,
      providerErrorCode: data.provider_error_code,
      createdAt: data.created_at,
      reviews: data.reviews.map((review) => ({
        id: review.id,
        reviewerId: review.reviewer_id,
        verdict: review.verdict,
        comment: review.comment,
        createdAt: review.created_at,
      })),
    }
  }

  static fetch(data: ProtectionResponseApi): ProtectionResponse {
    return {
      timezone: data.timezone ?? 'UTC',
      mode: data.mode,
      from: data.from,
      to: data.to,
      lastObservationAt: data.last_observation_at,
      observations: { data: data.observations.data.map(this.observation), count: data.observations.total },
      volumeModel: data.volume_model
        ? {
            status: data.volume_model.status,
            limits: data.volume_model.limits,
            baselineFrom: data.volume_model.baseline_from,
            baselineTo: data.volume_model.baseline_to,
          }
        : undefined,
      summary: data.summary.map((row) => ({
        decision: row.decision,
        attempts: row.attempts,
        estimatedCostUsd: row.estimated_cost_usd,
        unpricedAttempts: row.unpriced_attempts,
      })),
      rules: {
        version: data.rules.version,
        limits: data.rules.limits,
        duplicateSeconds: data.rules.duplicate_seconds,
        allowedCountries: data.rules.allowed_countries,
        tariffsUsd: data.rules.tariffs_usd,
        tariffVersion: data.rules.tariff_version,
      },
    }
  }

  static twilio(data: TwilioComparisonResponseApi): TwilioComparisonResponse {
    return {
      timezone: data.timezone ?? 'UTC',
      from: data.from,
      to: data.to,
      weeklyEnabled: data.weekly_enabled,
      credentialsConfigured: data.credentials_configured,
      stale: data.stale,
      schedule: data.schedule,
      lastSuccessAt: data.last_success_at,
      internalCoverage: {
        observations: data.internal_coverage.observations,
        withoutTwilioMatch: data.internal_coverage.without_twilio_match,
      },
      lastRun: data.last_run && !Array.isArray(data.last_run)
        ? {
            source: data.last_run.source,
            status: data.last_run.status,
            finishedAt: data.last_run.finished_at,
            windowFrom: data.last_run.window_from,
            windowTo: data.last_run.window_to,
            coverageNote: data.last_run.coverage_note,
            error: data.last_run.error,
          }
        : null,
      summary: data.summary.map((row) => ({
        outcome: row.outcome,
        internalDecision: row.internal_decision,
        total: row.total,
      })),
      messages: {
        count: data.messages.total,
        data: data.messages.data.map((row) => ({
          sid: row.sid,
          status: row.status,
          errorCode: row.error_code,
          outcome: row.outcome,
          messageAt: row.message_at,
          syncedAt: row.synced_at,
          internalDecision: row.internal_decision,
          observationCount: row.observation_count,
          internalObservation: row.internal_observation && !Array.isArray(row.internal_observation)
            ? {
                id: row.internal_observation.id,
                ruleVersion: row.internal_observation.rule_version,
                reasons: row.internal_observation.reasons,
                evidence: row.internal_observation.evidence,
              }
            : null,
        })),
      },
    }
  }
}
